import { todayInSaoPaulo } from "./dates";
import { isValidCNPJ, normalizeDocument } from "./documents";

/**
 * Certificado digital A1 (T2.1, RF-PRF-08): regras puras de CNPJ do titular, validade e estágios de alerta. A leitura
 * do PKCS#12 (ASN.1, decifragem) mora no Worker; aqui só o que a tela, o DO e o cron decidem.
 */

/** Teto do arquivo .pfx/.p12: um A1 com a cadeia tem 3–10 KB. */
export const MAX_CERTIFICATE_BYTES = 64 * 1024;
/** Teto de iterações de PBKDF/MAC (Windows 2.000, OpenSSL 2.048, Java 10.000): acima disso é recusado. */
export const MAX_PBE_ITERATIONS = 100_000;
/**
 * Teto da SOMA das iterações de todas as derivações de chave de um .pfx (MAC + cada camada PBE, inclusive as que só
 * aparecem depois de decifrar; o PBE do PKCS#12 deriva chave e IV, 2 vezes). Um A1 real gasta ~10.000 (5 × 2.048); o
 * teto aceita PBES2 com 100.000 nas 3 camadas e segura a CPU do Worker num .pfx com dezenas de camadas.
 */
export const MAX_KDF_WORK = 300_000;
/** Teto de derivações de chave por .pfx (um A1 real: 3 no PBES2, 5 no legado). */
export const MAX_KDF_CALLS = 8;
/** Dias antes do vencimento em que o alerta dispara (e no dia do vencimento: estágio 0). */
export const CERT_ALERT_DAYS = [30, 15, 7, 1] as const;
/** Estágio do alerta: um dos limiares ou 0 (venceu). */
export type CertAlertStage = (typeof CERT_ALERT_DAYS)[number] | 0;

/** CNPJ (numérico ou alfanumérico) com DV válido, normalizado; senão null. */
export function normalizeCertificateCnpj(raw: string): string | null {
  const c = normalizeDocument(raw);
  return c.length === 14 && isValidCNPJ(c) ? c : null;
}

/** e-CNPJ: o CN termina em `:<CNPJ>` ("RAZAO SOCIAL LTDA:11222333000181"). */
export function cnpjFromCommonName(cn: string): string | null {
  const m = /:([0-9A-Za-z]{14})$/.exec(cn.trim());
  return m ? normalizeCertificateCnpj(m[1]!) : null;
}

/** Raiz do CNPJ (8 primeiros caracteres, sem pontuação). */
export const cnpjRoot = (cnpj: string): string => normalizeDocument(cnpj).slice(0, 8);

/** Mesma raiz (matriz/filial): a SEFAZ aceita o e-CNPJ da matriz na nota da filial (rejeição 213). */
export function sameCnpjRoot(a: string, b: string | null | undefined): boolean {
  if (!b) return false;
  const ra = cnpjRoot(a);
  return ra.length === 8 && ra === cnpjRoot(b);
}

const DAY_MS = 86_400_000;
const civilMs = (iso: string) => Date.parse(`${iso}T00:00:00Z`);

/** Dias de calendário (São Paulo) de hoje até o dia do vencimento; negativo = venceu há tantos dias. */
export function certificateDaysLeft(validTo: string, now: Date = new Date()): number {
  const end = todayInSaoPaulo(new Date(validTo));
  return Math.round((civilMs(end) - civilMs(todayInSaoPaulo(now))) / DAY_MS);
}

/**
 * Estágio do alerta para `daysLeft` (o menor limiar alcançado; 0 no dia do vencimento ou depois). Com `last` (o
 * último estágio já avisado), só devolve um estágio NOVO, mais próximo do vencimento; senão null.
 */
export function certificateAlertStage(daysLeft: number, last?: number | null): CertAlertStage | null {
  let stage: CertAlertStage | null = null;
  if (daysLeft <= 0) stage = 0;
  else for (const d of CERT_ALERT_DAYS) if (daysLeft <= d) stage = d;
  if (stage === null) return null;
  return last == null || stage < last ? stage : null;
}

export type CertificateState = { kind: "valid" | "expiring" | "expired"; daysLeft: number };

/** Situação para a tela: vencido pelo instante; "vencendo" a até 30 dias (inclusive no último dia). */
export function certificateState(validTo: string, now: Date = new Date()): CertificateState {
  const daysLeft = certificateDaysLeft(validTo, now);
  if (Date.parse(validTo) <= now.getTime()) return { kind: "expired", daysLeft: Math.min(0, daysLeft) };
  return { kind: daysLeft <= CERT_ALERT_DAYS[0] ? "expiring" : "valid", daysLeft };
}
